import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Box, Button, Tab, Tabs, Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import RejectReasonModal from "../../Modals/rejectReason";
import PropertyList from "../../features/manageCustomers/propertyList";
import Loader from "../../constants/Loader";
import { showError } from "../../constants/toasts";
import { EmployeData, Employer, Tasks } from "../../types/General";
import {
  useLazyGetEmployerByIdQuery,
  useLazyGetTaskListQuery,
} from "../../services/users";
import useTranslation from "../../hooks/traslation";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageCustomerDetail = () => {
  const { id } = useParams();
  const translation = useTranslation();

  const navigate = useNavigate();
  const [getDataById] = useLazyGetEmployerByIdQuery();
  const [getTaskById] = useLazyGetTaskListQuery();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") || "";

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<Employer | null>(null);
  const [data, setData] = useState<EmployeData | null>(null);
  const [rows, setRows] = useState<Tasks[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const [valueTabs, setValueTabs] = useState(tab ? Number(tab) : 0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setSearchParams({ tab: String(newValue) });
  };
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState(0);
  const handleCloseModal = () => {
    setOpen(false);
  };
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };
  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 150,
      field: "name",
      headerName: "Task Name",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <p>
              {row?.category?.name || "-"}, {row?.subCategory?.name || "-"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "type",
      headerName: "Category",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <p>{row?.category?.name || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 2,
      minWidth: 280,
      field: "address",
      headerName: "Sub Service",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <p>{row?.subCategory?.name || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 120,
      field: "city",
      headerName: "Ad-Ons",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <p>{row?.serviceNames?.toString() || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 120,
      field: "amount",
      headerName: "Amount",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <p className="c_success">
            <strong>AED {Number(row?.total || "0").toFixed(2)}</strong>
          </p>
        );
      },
    },
    {
      flex: 1,
      minWidth: 120,
      field: "issues",
      headerName: "Detail",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Button
            className="btn sm btn_primary"
            onClick={() => navigate(`/task-detail/${id}/${row?._id}`)}
          >
            View
          </Button>
        );
      },
    },
  ];

  const getData = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDataById({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setDetail(response?.data?.user || null);
        setData(response?.data);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };

  const getTaskOfEmployer = async (id: string) => {
    setLoading(true);
    try {
      const response = await getTaskById({
        id,
        page,
        limit: 10,
        search: debouncedSearchTerm,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setRows(response?.data?.list || []);
        setCount(response?.data?.totalCount || null);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getData(id);
    }
  }, []);

  useEffect(() => {
    if (id && valueTabs) {
      getTaskOfEmployer(id);
    }
  }, [debouncedSearchTerm, id, valueTabs]);

  return (
    <>
      <MainContainer>
        <Loader isLoading={loading} />
        <div className="main_title">
          <h1>{translation.main.employerDetail}</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>
              {translation.dashboard.dashboard}
            </span>{" "}
            -{" "}
            <span onClick={() => navigate("/manage-employers")}>
              {translation.main.ManageEmployer}
            </span>{" "}
            - {translation.main.employerDetail}
          </p>
        </div>

        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: "100%" }}>
              <Tabs
                value={valueTabs}
                onChange={handleChangeTabs}
                aria-label="basic tabs example"
                className="custom_tabs_links"
              >
                <Tab label={translation.main.generalDetail} {...a11yProps(0)} />
                <Tab label={translation.main.taskList} {...a11yProps(1)} />
              </Tabs>
            </Box>
          </div>
        </div>
        <CustomTabPanel value={valueTabs} index={0}>
          <div className="cards view_page">
            <div className="section_title">
              <h2>{translation.main.personalDetail}</h2>
            </div>
            <div className="gap_p">
              <div className="vew_img w_100">
                <h6>{translation.main.Image}</h6>
                <figure>
                  <img
                    alt="user"
                    src={detail?.image || "/static/images/user_placeholder.png"}
                  />
                </figure>
              </div>
              <div className="w_33">
                <h6>{translation.globals.name}</h6>
                <p>
                  {detail?.firstName || "-"} {detail?.lastName || ""}
                </p>
              </div>
              <div className="w_33">
                <h6>{translation.main.email}</h6>
                <p>{detail?.email || ""}</p>
              </div>
              <div className="w_33">
                <h6>{translation.main.phone}</h6>
                <p>
                  {detail?.dialCode || "-"} {detail?.phoneNo || ""}
                </p>
              </div>
              <div className="w_33">
                <h6>Address Name</h6>
                <p>{data?.address?.addressName || "-"}</p>
              </div>
              <div className="w_33">
                <h6>House No</h6>
                <p>{data?.address?.houseNo || "-"}</p>
              </div>
              <div className="w_33">
                <h6>Block</h6>
                <p>{data?.address?.block || "-"}</p>
              </div>
              <div className="w_33">
                <h6>Street</h6>
                <p>{data?.address?.street || "-"}</p>
              </div>
              <div className="w_33">
                <h6>Area</h6>
                <p>{data?.address?.area || "-"}</p>
              </div>
              <div className="w_33">
                <h6>Country</h6>
                <p>{data?.address?.country || "-"}</p>
              </div>
              <div className="w_33">
                <h6>Phone</h6>
                <p>
                  {data?.address?.dialCode || "-"}{" "}
                  {data?.address?.phoneNo || "-"}
                </p>
              </div>

              <div className="w_33">
                <h6>Note</h6>
                <p>{data?.address?.note || "-"}</p>
              </div>
            </div>
            {data?.address?.latitude && data?.address?.longitude && (
              <div className="w_33">
                {/* <div className="borderBox"> */}
                <div className="section_title">{/* <h2>Map</h2> */}</div>
                <div
                  style={{
                    position: "relative",
                    height: "150px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    width: "300px",
                  }}
                >
                  <iframe
                    src={`https://www.google.com/maps?q=${data?.address?.latitude},${data?.address?.longitude}&z=15&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
              // </div>
            )}
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <div className="cards">
            <Box className="search_box" style={{ marginBottom: "20px" }}>
              <SearchBar
                placeholder={translation.user.SearchHere}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
            <div className="sorting_table">
              <TableComponent
                columns={columns}
                rows={rows}
                count={count}
                page={page}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </div>
          </div>
        </CustomTabPanel>
      </MainContainer>

      <RejectReasonModal
        open={open}
        onClose={handleCloseModal}
        setOpen={setOpen}
      />
    </>
  );
};

export default ManageCustomerDetail;
